import { Avatar } from "@/components/primitives";
import { User, Bot, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface Message {
	id: string;
	type: "user" | "assistant";
	content: string;
	timestamp: Date;
}

interface ChatMessageProps {
	message: Message;
	isLoading?: boolean;
}

export function ChatMessage({ message, isLoading = false }: ChatMessageProps) {
	const isUser = message.type === "user";

	return (
		<div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
			{!isUser && (
				<Avatar className="h-8 w-8 shrink-0">
					<div className="flex h-full w-full items-center justify-center bg-primary/10">
						<Bot className="h-4 w-4 text-primary" />
					</div>
				</Avatar>
			)}

			<div
				className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
					isUser
						? "bg-primary text-primary-foreground ml-auto"
						: "bg-muted text-muted-foreground"
				}`}
			>
				{isLoading ? (
					<div className="flex items-center space-x-2">
						<Loader2 className="h-3 w-3 animate-spin" />
						<span>{message.content}</span>
					</div>
				) : isUser ? (
					// User messages: plain text (no markdown needed)
					<div className="whitespace-pre-wrap break-words">
						{message.content}
					</div>
				) : (
					// Assistant messages: render markdown
					<div className="prose prose-sm max-w-none dark:prose-invert break-words">
						<ReactMarkdown
							remarkPlugins={[remarkGfm]}
							components={{
								p: ({ children }) => (
									<p className="mb-2 last:mb-0">{children}</p>
								),
								ul: ({ children }) => (
									<ul className="list-disc pl-4 mb-2">{children}</ul>
								),
								ol: ({ children }) => (
									<ol className="list-decimal pl-4 mb-2">{children}</ol>
								),
								li: ({ children }) => <li className="mb-1">{children}</li>,
								strong: ({ children }) => (
									<strong className="font-semibold">{children}</strong>
								),
								em: ({ children }) => <em className="italic">{children}</em>,
							}}
						>
							{message.content}
						</ReactMarkdown>
					</div>
				)}

				<div
					className={`text-xs mt-1 ${isUser ? "text-primary-foreground/70" : "text-muted-foreground/70"}`}
				>
					{message.timestamp.toLocaleTimeString([], {
						hour: "2-digit",
						minute: "2-digit",
					})}
				</div>
			</div>

			{isUser && (
				<Avatar className="h-8 w-8 shrink-0">
					<div className="flex h-full w-full items-center justify-center bg-secondary">
						<User className="h-4 w-4 text-secondary-foreground" />
					</div>
				</Avatar>
			)}
		</div>
	);
}
